import React from 'react';
import { Card, CardContent } from '@/components/ui/card';

/**
 * Metric display primitives, shared so a number means the same thing wherever it
 * appears.
 *
 * The `tone` prop is the reason these are components rather than markup: green and
 * red are load-bearing on a trading screen, and "positive number" is NOT the same
 * question as "good outcome". Max drawdown is always red, a short entry price is
 * neither. Passing the tone explicitly keeps that judgement at the call site instead
 * of letting a `value >= 0` check invent it.
 */

export type StatTone = 'good' | 'bad' | 'neutral';

const toneClass = (tone: StatTone | undefined): string =>
  tone === 'good' ? 'text-emerald-500' : tone === 'bad' ? 'text-red-500' : '';

/** Derive a tone from a signed number, for the common case where positive IS good. */
export const toneOf = (value: number | null | undefined, threshold = 0): StatTone =>
  value == null ? 'neutral' : value >= threshold ? 'good' : 'bad';

// ── Card-wrapped headline stat ───────────────────────────────────

/** A single metric in its own card — for the 4-across summary grids. */
export const StatCard: React.FC<{
  label: string;
  value: string;
  tone?: StatTone;
  hint?: string;
}> = ({ label, value, tone, hint }) => (
  <Card className="transition-colors hover:border-muted-foreground/30">
    <CardContent className="p-3">
      <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${toneClass(tone)}`} title={hint}>
        {value}
      </div>
    </CardContent>
  </Card>
);

// ── Compact, unwrapped tile ──────────────────────────────────────

/** A metric inside an existing panel — no card chrome, optional leading icon. */
export const MetricTile: React.FC<{
  label: string;
  value: string;
  tone?: StatTone;
  icon?: React.ReactNode;
}> = ({ label, value, tone, icon }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground/80">
      {icon}<span className="truncate">{label}</span>
    </div>
    <div className={`truncate text-sm font-semibold tabular-nums ${toneClass(tone)}`}>{value}</div>
  </div>
);

// ── Label/value pair ─────────────────────────────────────────────

/**
 * The smallest unit: a tiny caption over a value. Used for card facts and price
 * levels alike. `accent` takes an arbitrary class because level colours (entry blue,
 * stop orange, target green) are chart-legend colours rather than good/bad tones.
 */
export const Fact: React.FC<{
  label: string;
  value: string;
  accent?: string;
  title?: string;
}> = ({ label, value, accent, title }) => (
  <div className="min-w-0">
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground/70">{label}</div>
    <div className={`truncate text-sm font-medium tabular-nums ${accent ?? 'text-foreground/90'}`} title={title ?? value}>
      {value}
    </div>
  </div>
);

// ── Proportion bar ───────────────────────────────────────────────

/**
 * Win/loss split as a bar. A proportion is read faster as a bar than as a percentage,
 * and the bar makes a 51% edge visibly different from a 90% one in a way two digits
 * do not.
 */
export const WinRateBar: React.FC<{
  winRate: number;
  winners?: number;
  losers?: number;
}> = ({ winRate, winners, losers }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
      <span>
        {winners != null && losers != null
          ? `${winners.toLocaleString()} wins · ${losers.toLocaleString()} losses`
          : 'Win rate'}
      </span>
      <span className="tabular-nums">{winRate.toFixed(1)}% win rate</span>
    </div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-red-500/25">
      <div
        className="h-full rounded-full bg-emerald-500 transition-all"
        style={{ width: `${Math.min(100, Math.max(0, winRate))}%` }}
      />
    </div>
  </div>
);
